import React, { useState, useEffect, useMemo } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import {
  Calendar,
  BarChart3,
  Activity,
  Languages,
  Library,
  AlertCircle,
} from "lucide-react";
import { dashboardApi, alphaDashboardApi } from "../api/client";
import {
  fmtNumber,
  fmtDateShort,
  langLabel,
  themeLabel,
  RANGE_PRESETS,
  rangeToParams,
  CHART_COLORS,
} from "../utils/format";

// -- helpers ------------------------------------------------------

const Skeleton = ({
  width = "100%",
  height = 20,
  borderRadius = 4,
  style = {},
}) => (
  <div
    className="skeleton-box"
    style={{
      display: "inline-block",
      width,
      height,
      borderRadius,
      ...style,
    }}
  />
);

const Empty = ({ children }) => (
  <div
    style={{
      padding: "48px 24px",
      textAlign: "center",
      color: "var(--ink-400)",
      fontFamily: "var(--serif)",
      fontStyle: "italic",
      fontSize: 16,
      background: "var(--paper-2)",
      borderRadius: 12,
      border: "1px dashed var(--ink-200)",
    }}
  >
    {children}
  </div>
);

// Custom tooltip — matching Dashboard
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      style={{
        background: "rgba(255, 255, 255, 0.85)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: "1px solid var(--ink-200)",
        borderRadius: 12,
        padding: "12px 16px",
        fontSize: 13,
        boxShadow: "0 8px 32px -8px rgba(0, 0, 0, 0.12)",
      }}
    >
      {label && (
        <div
          style={{
            fontWeight: 600,
            marginBottom: 8,
            fontFamily: "var(--serif)",
            color: "var(--ink-900)",
            fontSize: 14,
          }}
        >
          {label}
        </div>
      )}
      {payload.map((p, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
            marginTop: 4,
          }}
        >
          <span
            style={{
              width: 10,
              height: 10,
              background: p.color,
              borderRadius: 3,
              boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
            }}
          />
          <span style={{ color: "var(--ink-600)", fontWeight: 500 }}>
            {p.name}:
          </span>
          <span
            style={{
              color: "var(--ink-900)",
              fontWeight: 700,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {typeof p.value === "number" ? fmtNumber(p.value) : p.value}
          </span>
        </div>
      ))}
    </div>
  );
};

// Build a 7-day × 24-hour heatmap from hourly timeseries
const buildHeatmap = (timeseries) => {
  const grid = Array.from({ length: 7 }, () => Array(24).fill(0));
  for (const point of timeseries) {
    const date = new Date(point.bucket);
    if (isNaN(date)) continue;
    const dow = date.getDay();
    const hour = date.getHours();
    grid[dow][hour] += point.user_msgs || 0;
  }
  return grid;
};

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// -- main page ---------------------------------------------------

export default function Analytics() {
  const [testerType, setTesterType] = useState("beta");
  const [range, setRange] = useState("30d");
  const [hourly, setHourly] = useState([]);
  const [daily, setDaily] = useState([]);
  const [topics, setTopics] = useState([]);
  const [themes, setThemes] = useState([]);
  const [kmsRefs, setKmsRefs] = useState([]);
  const [headline, setHeadline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const api = testerType === "alpha" ? alphaDashboardApi : dashboardApi;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = rangeToParams(range);
    Promise.all([
      api.timeseries({ ...params, granularity: "hour" }),
      api.timeseries({ ...params, granularity: "day" }),
      api.topics({ ...params, limit: 30 }),
      api.themes(params),
      api.kmsRefs({ ...params, limit: 20 }),
      api.headline(params),
    ])
      .then(([h, d, tp, th, kr, hl]) => {
        if (cancelled) return;
        setHourly(h);
        setDaily(d);
        setTopics(tp);
        setThemes(th);
        setKmsRefs(kr);
        setHeadline(hl);
      })
      .catch(
        (e) => !cancelled && setError(e?.response?.data?.error || e.message),
      )
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [range, testerType]);

  const heatmap = useMemo(() => buildHeatmap(hourly), [hourly]);
  const heatmapMax = useMemo(() => {
    let m = 0;
    for (const row of heatmap) for (const v of row) if (v > m) m = v;
    return m || 1;
  }, [heatmap]);

  const langData = useMemo(() => {
    if (!headline?.language_distribution) return [];
    return Object.entries(headline.language_distribution).map(([k, v]) => ({
      language: langLabel(k),
      count: v,
    }));
  }, [headline]);

  return (
    <div style={{ width: "100%" }}>
      <style>{`
        @keyframes skeleton-loading {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .skeleton-box {
          background: linear-gradient(90deg, var(--ink-100, #f3f4f6) 25%, var(--ink-200, #e5e7eb) 50%, var(--ink-100, #f3f4f6) 75%);
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s infinite linear;
        }
      `}</style>

      {/* Hero */}
      <div
        style={{ position: "relative", marginBottom: 32, padding: "16px 0" }}
      >
        <div
          className="devanagari-watermark"
          style={{
            position: "absolute",
            top: -30,
            right: 20,
            fontSize: 180,
            background:
              "linear-gradient(135deg, rgba(194,108,27,0.08) 0%, rgba(194,108,27,0.01) 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            pointerEvents: "none",
            userSelect: "none",
            fontWeight: 800,
          }}
        >
          विश्लेषण
        </div>
        <div
          className="t-eyebrow"
          style={{ color: "var(--ink-500)", letterSpacing: "0.05em" }}
        >
          Sangathana Console / Analytics
        </div>
        <h1
          className="t-display"
          style={{
            fontSize: 36,
            marginTop: 8,
            marginBottom: 12,
            color: "var(--ink-900)",
          }}
        >
          Look closer.
        </h1>
        <p
          style={{
            color: "var(--ink-600)",
            fontFamily: "var(--serif)",
            fontSize: 16,
            fontStyle: "italic",
            maxWidth: 720,
            lineHeight: 1.5,
          }}
        >
          Drill into rhythms of usage, balance across themes, and how the corpus
          is being drawn upon.
        </p>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 32,
        }}
      >
        {/* Tester type toggle */}
        {(() => {
          const TABS = [
            { key: "beta", label: "Beta Testers" },
            { key: "alpha", label: "Alpha Testers" },
          ];
          const activeIdx = TABS.findIndex((t) => t.key === testerType);
          return (
            <div
              style={{
                display: "inline-flex",
                position: "relative",
                background: "var(--paper-2)",
                border: "1px solid var(--ink-200)",
                borderRadius: 12,
                padding: 4,
                boxShadow: "inset 0 2px 4px rgba(0,0,0,0.02)",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 4,
                  bottom: 4,
                  left: 4,
                  width: "calc(50% - 4px)",
                  background: "var(--saffron)",
                  borderRadius: 8,
                  transform: `translateX(${activeIdx * 100}%)`,
                  transition:
                    "transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1)",
                  boxShadow:
                    "0 2px 8px rgba(194,108,27,0.35), 0 1px 2px rgba(194,108,27,0.2)",
                }}
              />
              {TABS.map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setTesterType(key)}
                  style={{
                    position: "relative",
                    zIndex: 1,
                    padding: "8px 24px",
                    fontSize: 13,
                    fontWeight: 600,
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    borderRadius: 8,
                    color: testerType === key ? "#fff" : "var(--ink-600)",
                    transition: "color 250ms ease",
                    letterSpacing: "0.02em",
                    whiteSpace: "nowrap",
                    userSelect: "none",
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          );
        })()}

        {/* Range selector */}
        <div
          style={{
            display: "inline-flex",
            background: "var(--paper-2)",
            padding: 4,
            borderRadius: 12,
            border: "1px solid var(--ink-200)",
            boxShadow: "inset 0 2px 4px rgba(0,0,0,0.02)",
          }}
        >
          {Object.entries(RANGE_PRESETS).map(([k, p]) => (
            <button
              key={k}
              onClick={() => setRange(k)}
              style={{
                padding: "8px 16px",
                fontSize: 13,
                background: range === k ? "#ffffff" : "transparent",
                border: "none",
                borderRadius: 8,
                color: range === k ? "var(--saffron-deep)" : "var(--ink-600)",
                fontWeight: range === k ? 600 : 500,
                boxShadow: range === k ? "0 1px 3px rgba(0,0,0,0.06)" : "none",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: "16px 20px",
            borderRadius: 12,
            border: "1px solid var(--red)",
            background: "#fff1f2",
            color: "var(--red)",
            marginBottom: 24,
            display: "flex",
            alignItems: "center",
            gap: 12,
            fontWeight: 500,
          }}
        >
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {/* Time-of-day heatmap */}
      <div
        className="panel"
        style={{
          background: "#ffffff",
          borderRadius: 16,
          border: "1px solid var(--ink-100)",
          boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.02)",
          overflow: "hidden",
          marginBottom: 32,
        }}
      >
        <div
          className="panel-header"
          style={{
            padding: "24px 28px",
            borderBottom: "1px solid var(--ink-100)",
            background: "rgba(251, 246, 238, 0.3)",
          }}
        >
          <div
            className="t-eyebrow"
            style={{ color: "var(--ink-500)", marginBottom: 6 }}
          >
            Samay-chakra
          </div>
          <div
            className="t-display"
            style={{
              fontSize: 24,
              display: "flex",
              alignItems: "center",
              gap: 10,
              color: "var(--ink-900)",
            }}
          >
            <Calendar size={22} style={{ color: "var(--saffron-deep)" }} />
            When seekers ask
          </div>
        </div>
        <div
          className="panel-body"
          style={{ padding: "28px", overflowX: "auto" }}
        >
          {loading ? (
            <div style={{ minWidth: 720, width: "100%" }}>
              <div
                style={{ display: "flex", marginBottom: 8, paddingLeft: 62 }}
              >
                {[...Array(24)].map((_, i) => (
                  <div key={i} style={{ flex: 1, padding: "0 2px" }}>
                    <Skeleton height={14} />
                  </div>
                ))}
              </div>
              {[...Array(7)].map((_, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    marginBottom: 4,
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{ width: 50, marginRight: 12, textAlign: "right" }}
                  >
                    <Skeleton
                      height={14}
                      width={30}
                      style={{ display: "inline-block" }}
                    />
                  </div>
                  {[...Array(24)].map((_, j) => (
                    <div key={j} style={{ flex: 1, padding: "0 2px" }}>
                      <Skeleton height={28} borderRadius={4} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <>
              <table
                style={{
                  borderCollapse: "collapse",
                  minWidth: 720,
                  fontSize: 11,
                  width: "100%",
                }}
              >
                <thead>
                  <tr>
                    <th style={{ width: 50 }}></th>
                    {Array.from({ length: 24 }).map((_, h) => (
                      <th
                        key={h}
                        style={{
                          fontWeight: 600,
                          color: "var(--ink-400)",
                          fontSize: 11,
                          padding: "0 0 8px",
                          textAlign: "center",
                        }}
                      >
                        {h % 3 === 0 ? h : ""}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {DAYS.map((day, dow) => (
                    <tr key={day}>
                      <td
                        style={{
                          paddingRight: 12,
                          textAlign: "right",
                          fontSize: 11,
                          color: "var(--ink-500)",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                        }}
                      >
                        {day}
                      </td>
                      {heatmap[dow].map((v, h) => {
                        const intensity = v / heatmapMax;
                        return (
                          <td key={h} style={{ padding: 2 }}>
                            <div
                              title={`${day} ${h}:00 — ${v} queries`}
                              style={{
                                width: "100%",
                                height: 28,
                                borderRadius: 4,
                                minWidth: 20,
                                background:
                                  v === 0
                                    ? "var(--paper-2)"
                                    : `rgba(194, 108, 27, ${0.12 + intensity * 0.88})`,
                                border: "1px solid rgba(194, 108, 27, 0.05)",
                                transition: "transform 0.1s ease",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "scale(1.1)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "scale(1)";
                              }}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div
                style={{
                  marginTop: 20,
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 12,
                  color: "var(--ink-500)",
                  fontWeight: 500,
                }}
              >
                <span>Less</span>
                {[0.1, 0.3, 0.5, 0.7, 0.9].map((t) => (
                  <div
                    key={t}
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 3,
                      background: `rgba(194, 108, 27, ${0.12 + t * 0.88})`,
                      border: "1px solid rgba(194, 108, 27, 0.05)",
                    }}
                  />
                ))}
                <span>More</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Daily trend (line) */}
      <div
        className="panel"
        style={{
          background: "#ffffff",
          borderRadius: 16,
          border: "1px solid var(--ink-100)",
          boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.02)",
          overflow: "hidden",
          marginBottom: 32,
        }}
      >
        <div
          className="panel-header"
          style={{
            padding: "24px 28px",
            borderBottom: "1px solid var(--ink-100)",
            background: "rgba(251, 246, 238, 0.3)",
          }}
        >
          <div
            className="t-eyebrow"
            style={{ color: "var(--ink-500)", marginBottom: 6 }}
          >
            Pravritti
          </div>
          <div
            className="t-display"
            style={{
              fontSize: 24,
              display: "flex",
              alignItems: "center",
              gap: 10,
              color: "var(--ink-900)",
            }}
          >
            <Activity size={22} style={{ color: "var(--saffron-deep)" }} />
            Daily message volume
          </div>
        </div>
        <div className="panel-body" style={{ padding: "28px" }}>
          {loading ? (
            <Skeleton height={320} borderRadius={8} />
          ) : !daily.length ? (
            <Empty>No daily data in this period.</Empty>
          ) : (
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <LineChart
                  data={daily}
                  margin={{ top: 12, right: 16, bottom: 12, left: -16 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--ink-100)"
                    vertical={false}
                    strokeOpacity={0.8}
                  />
                  <XAxis
                    dataKey="bucket"
                    tickFormatter={fmtDateShort}
                    tick={{ fontSize: 12, fill: "var(--ink-500)" }}
                    axisLine={{ stroke: "var(--ink-200)" }}
                    tickLine={false}
                    dy={12}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: "var(--ink-400)" }}
                    axisLine={false}
                    tickLine={false}
                    dx={-8}
                  />
                  <Tooltip
                    content={<ChartTooltip />}
                    cursor={{
                      stroke: "var(--ink-200)",
                      strokeWidth: 1.5,
                      strokeDasharray: "4 4",
                    }}
                  />
                  <Legend
                    wrapperStyle={{
                      fontSize: 13,
                      paddingTop: 16,
                      fontWeight: 500,
                      color: "var(--ink-700)",
                    }}
                    iconType="circle"
                  />
                  <Line
                    type="monotone"
                    dataKey="user_msgs"
                    name="User queries"
                    stroke="var(--saffron)"
                    strokeWidth={3}
                    dot={{ r: 3, fill: "var(--saffron)", strokeWidth: 0 }}
                    activeDot={{ r: 6, stroke: "#fff", strokeWidth: 2 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="assistant_msgs"
                    name="Assistant replies"
                    stroke="var(--green)"
                    strokeWidth={3}
                    dot={{ r: 3, fill: "var(--green)", strokeWidth: 0 }}
                    activeDot={{ r: 6, stroke: "#fff", strokeWidth: 2 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="unanswered"
                    name="Unanswered"
                    stroke="var(--red)"
                    strokeWidth={2}
                    strokeDasharray="4 4"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Theme breakdown + Language */}
      <div
        style={{
          display: "grid",
          gap: 24,
          marginBottom: 32,
          gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
        }}
      >
        <div
          className="panel"
          style={{
            background: "#ffffff",
            borderRadius: 16,
            border: "1px solid var(--ink-100)",
            boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.02)",
            overflow: "hidden",
          }}
        >
          <div
            className="panel-header"
            style={{
              padding: "24px 28px",
              borderBottom: "1px solid var(--ink-100)",
              background: "rgba(251, 246, 238, 0.3)",
            }}
          >
            <div
              className="t-eyebrow"
              style={{ color: "var(--ink-500)", marginBottom: 6 }}
            >
              Vichar-prakar
            </div>
            <div
              className="t-display"
              style={{
                fontSize: 24,
                display: "flex",
                alignItems: "center",
                gap: 10,
                color: "var(--ink-900)",
              }}
            >
              <BarChart3 size={22} style={{ color: "var(--saffron-deep)" }} />
              Theme distribution
            </div>
          </div>
          <div className="panel-body" style={{ padding: "28px" }}>
            {loading ? (
              <Skeleton height={320} borderRadius={8} />
            ) : !themes.length ? (
              <Empty>No theme data.</Empty>
            ) : (
              <div style={{ width: "100%", height: 320 }}>
                <ResponsiveContainer>
                  <BarChart
                    data={themes.map((t) => ({
                      ...t,
                      label: themeLabel(t.theme),
                    }))}
                    layout="vertical"
                    margin={{ top: 8, right: 32, bottom: 8, left: 16 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="var(--ink-100)"
                      horizontal={false}
                      strokeOpacity={0.7}
                    />
                    <XAxis
                      type="number"
                      tick={{ fontSize: 12, fill: "var(--ink-400)" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="label"
                      width={170}
                      tick={{
                        fontSize: 13,
                        fill: "var(--ink-700)",
                        fontWeight: 500,
                      }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      content={<ChartTooltip />}
                      cursor={{ fill: "var(--paper-2)", opacity: 0.6 }}
                    />
                    <Bar
                      dataKey="count"
                      fill="var(--saffron)"
                      radius={[0, 6, 6, 0]}
                      barSize={24}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        <div
          className="panel"
          style={{
            background: "#ffffff",
            borderRadius: 16,
            border: "1px solid var(--ink-100)",
            boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.02)",
            overflow: "hidden",
          }}
        >
          <div
            className="panel-header"
            style={{
              padding: "24px 28px",
              borderBottom: "1px solid var(--ink-100)",
              background: "rgba(251, 246, 238, 0.3)",
            }}
          >
            <div
              className="t-eyebrow"
              style={{ color: "var(--ink-500)", marginBottom: 6 }}
            >
              Bhasha
            </div>
            <div
              className="t-display"
              style={{
                fontSize: 24,
                display: "flex",
                alignItems: "center",
                gap: 10,
                color: "var(--ink-900)",
              }}
            >
              <Languages size={22} style={{ color: "var(--saffron-deep)" }} />
              Languages
            </div>
          </div>
          <div className="panel-body" style={{ padding: "28px" }}>
            {loading ? (
              <Skeleton height={320} borderRadius={8} />
            ) : !langData.length ? (
              <Empty>No language data.</Empty>
            ) : (
              <div style={{ width: "100%", height: 320 }}>
                <ResponsiveContainer>
                  <BarChart
                    data={langData}
                    margin={{ top: 8, right: 16, bottom: 8, left: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="var(--ink-100)"
                      vertical={false}
                      strokeOpacity={0.7}
                    />
                    <XAxis
                      dataKey="language"
                      tick={{
                        fontSize: 12,
                        fill: "var(--ink-700)",
                        fontWeight: 500,
                      }}
                      axisLine={false}
                      tickLine={false}
                      dy={8}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: "var(--ink-400)" }}
                      axisLine={false}
                      tickLine={false}
                      dx={-8}
                    />
                    <Tooltip
                      content={<ChartTooltip />}
                      cursor={{ fill: "var(--paper-2)", opacity: 0.6 }}
                    />
                    <Bar
                      dataKey="count"
                      fill="var(--green)"
                      radius={[6, 6, 0, 0]}
                      maxBarSize={60}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* KMS reference utilization */}
      {/* <div
        className="panel"
        style={{
          background: "#ffffff",
          borderRadius: 16,
          border: "1px solid var(--ink-100)",
          boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.02)",
          overflow: "hidden",
        }}
      >
        <div
          className="panel-header"
          style={{
            padding: "24px 28px",
            borderBottom: "1px solid var(--ink-100)",
            background: "rgba(251, 246, 238, 0.3)",
          }}
        >
          <div
            className="t-eyebrow"
            style={{ color: "var(--ink-500)", marginBottom: 6 }}
          >
            Adhar-granth utilisation
          </div>
          <div
            className="t-display"
            style={{
              fontSize: 24,
              display: "flex",
              alignItems: "center",
              gap: 10,
              color: "var(--ink-900)",
            }}
          >
            <Library size={22} style={{ color: "var(--saffron-deep)" }} />
            How often each reference is drawn upon
          </div>
        </div>
        <div className="panel-body" style={{ padding: 0 }}>
          {loading ? (
            <div>
              <div
                style={{
                  display: "flex",
                  padding: "12px 28px",
                  borderBottom: "1px solid var(--ink-200)",
                  background: "var(--paper-2)",
                }}
              >
                <Skeleton width={80} height={16} />
                <div style={{ flex: 1 }} />
                <Skeleton width={60} height={16} style={{ marginRight: 28 }} />
                <Skeleton width={130} height={16} style={{ marginRight: 28 }} />
                <Skeleton width={60} height={16} />
              </div>
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "16px 28px",
                    borderBottom: i === 4 ? "none" : "1px solid var(--ink-100)",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <Skeleton
                      width="60%"
                      height={18}
                      style={{ marginBottom: 4 }}
                    />
                    <Skeleton width={120} height={14} />
                  </div>
                  <Skeleton
                    width={40}
                    height={16}
                    style={{ marginLeft: 24, marginRight: 28 }}
                  />
                  <Skeleton
                    width={130}
                    height={8}
                    borderRadius={4}
                    style={{ marginRight: 28 }}
                  />
                  <Skeleton width={40} height={16} />
                </div>
              ))}
            </div>
          ) : !kmsRefs.length ? (
            <div style={{ padding: "48px 24px" }}>
              <Empty>No KMS references cited yet.</Empty>
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr
                  style={{
                    borderBottom: "1px solid var(--ink-200)",
                    background: "var(--paper-2)",
                  }}
                >
                  <th
                    style={{
                      padding: "12px 28px",
                      textAlign: "left",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "var(--ink-500)",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Reference
                  </th>
                  <th
                    style={{
                      width: 100,
                      textAlign: "right",
                      padding: "12px 28px",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "var(--ink-500)",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Usage
                  </th>
                  <th style={{ width: 130 }}></th>
                  <th
                    style={{
                      width: 110,
                      textAlign: "right",
                      padding: "12px 28px",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "var(--ink-500)",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Avg score
                  </th>
                </tr>
              </thead>
              <tbody>
                {kmsRefs.map((r, i) => {
                  const max = Math.max(...kmsRefs.map((x) => x.usage_count));
                  const pct = max ? (r.usage_count / max) * 100 : 0;
                  return (
                    <tr
                      key={r.external_id}
                      style={{
                        borderBottom:
                          i === kmsRefs.length - 1
                            ? "none"
                            : "1px solid var(--ink-100)",
                        transition: "background 0.15s ease",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "var(--paper-2)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      <td style={{ padding: "16px 28px" }}>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 14,
                            color: "var(--ink-900)",
                            marginBottom: 4,
                          }}
                        >
                          {r.title}
                        </div>
                        <code
                          style={{
                            fontSize: 11,
                            color: "var(--ink-400)",
                            fontFamily: "var(--mono)",
                            background: "var(--ink-100)",
                            padding: "2px 6px",
                            borderRadius: 4,
                          }}
                        >
                          {r.external_id}
                        </code>
                      </td>
                      <td
                        style={{
                          padding: "16px 28px",
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                          fontWeight: 500,
                          color: "var(--ink-700)",
                        }}
                      >
                        {fmtNumber(r.usage_count)}
                      </td>
                      <td style={{ padding: "16px 0" }}>
                        <div
                          style={{
                            height: 8,
                            background: "var(--ink-100)",
                            borderRadius: 4,
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              width: pct + "%",
                              height: "100%",
                              background: "var(--saffron)",
                              borderRadius: 4,
                              transition:
                                "width 600ms cubic-bezier(0.4, 0, 0.2, 1)",
                            }}
                          />
                        </div>
                      </td>
                      <td
                        style={{
                          padding: "16px 28px",
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                          color: "var(--ink-500)",
                          fontWeight: 500,
                        }}
                      >
                        {(r.avg_relevance || 0).toFixed(3)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div> */}
    </div>
  );
}
